/**
 * O cadastro da empresa e o contato financeiro, com a edição no lugar (#769).
 *
 * Como o contrato: editar abre os campos dentro da própria ficha, sem diálogo.
 * São dois cartões, porque são duas coisas: a empresa (razão social, CNPJ e
 * segmento) e a pessoa que a equipe procura para a cobrança. Cada um salva o
 * seu, e o histórico registra a mudança da empresa com antes e depois e a do
 * contato só como incluído, trocado ou removido, porque é dado pessoal.
 *
 * Na organização excluída nada se edita: a gestão dela acabou. Só o contato
 * financeiro, que fica guardado até a purga sem aparecer, ainda se apaga, a
 * pedido de quem foi indicado. Apagar não se desfaz: pede confirmação antes.
 */

import { useState, type ReactNode } from 'react';
import { Botao, Campo, CampoEscolha, Cartao, Ficha } from '@/shared/ui';
import { mascararCnpj } from '@/shared/lib/cnpj';
import { diaMesAno, telefoneParaLer } from '@/shared/lib/formato';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import {
  camposDoCadastro,
  camposDoContato,
  lerCadastro,
  lerContato,
  type CamposDoCadastro,
  type CamposDoContato,
  type Erros,
} from '../lib/cadastro';
import {
  SEGMENTOS,
  type MudancaDoCadastro,
  type OrganizacaoDetalhada,
} from '../model/organizacao-detalhada';

const OPCOES_DE_SEGMENTO = SEGMENTOS.map((segmento) => ({
  valor: segmento.valor,
  rotulo: segmento.nome,
}));

/** Os botões do cabeçalho de um cartão: editar, ou cancelar e salvar. */
function AcoesDaEdicao({
  editando,
  salvando,
  rotulo,
  aoAbrir,
  aoCancelar,
  aoSalvar,
}: {
  editando: boolean;
  salvando: boolean;
  rotulo: string;
  aoAbrir: () => void;
  aoCancelar: () => void;
  aoSalvar: () => void;
}): ReactNode {
  if (!editando) {
    return (
      <Botao miudo onClick={aoAbrir}>
        {rotulo}
      </Botao>
    );
  }
  return (
    <>
      <Botao
        variante="fantasma"
        miudo
        desabilitado={salvando}
        motivo="Salvando"
        onClick={aoCancelar}
      >
        Cancelar
      </Botao>
      <Botao variante="primario" miudo desabilitado={salvando} motivo="Salvando" onClick={aoSalvar}>
        {salvando ? 'Salvando…' : 'Salvar'}
      </Botao>
    </>
  );
}

/**
 * A edição de um cartão: os campos, os erros, a falha do servidor e o
 * "salvando". O que estava nos campos quando a edição abriu fica guardado: o
 * pedido leva só o que mudou disso.
 */
function useEdicao<C extends Record<string, string>>(
  ler: (
    campos: C,
    abertos: C,
  ) => { ok: true; mudanca: MudancaDoCadastro } | { ok: false; erros: Erros<C> },
  aoSalvar: ((mudanca: MudancaDoCadastro) => Promise<void>) | undefined,
  falhaGenerica: string,
) {
  const [abertos, definirAbertos] = useState<C | null>(null);
  const [campos, definirCampos] = useState<C | null>(null);
  const [erros, definirErros] = useState<Erros<C>>({});
  const [falha, definirFalha] = useState<string | null>(null);
  const [salvando, definirSalvando] = useState(false);

  const abrir = (iniciais: C) => {
    definirAbertos(iniciais);
    definirCampos(iniciais);
    definirErros({});
    definirFalha(null);
  };
  const fechar = () => {
    definirAbertos(null);
    definirCampos(null);
    // A falha de um salvamento recusado não fica sobre os valores de antes (#1160).
    definirFalha(null);
  };
  const mudar = (campo: keyof C, valor: string) =>
    definirCampos((antes) => (antes ? { ...antes, [campo]: valor } : antes));

  /**
   * Manda a mudança ao servidor: deu certo, a edição fecha; não deu, o motivo
   * aparece. Diz se deu certo, para quem pediu decidir o que fecha.
   */
  const enviar = async (mudanca: MudancaDoCadastro): Promise<boolean> => {
    if (!aoSalvar) return false;
    definirErros({});
    definirFalha(null);
    definirSalvando(true);
    try {
      await aoSalvar(mudanca);
      fechar();
      return true;
    } catch (erro) {
      definirFalha(recadoDeErro(erro, { generica: falhaGenerica }));
      return false;
    } finally {
      definirSalvando(false);
    }
  };
  const limparFalha = () => definirFalha(null);

  const salvar = async () => {
    if (!campos || !abertos) return;
    const leitura = ler(campos, abertos);
    if (!leitura.ok) {
      definirErros(leitura.erros);
      return;
    }
    // Nada mudou: a edição fecha sem pedir nada ao servidor.
    if (Object.keys(leitura.mudanca).length === 0) {
      fechar();
      return;
    }
    await enviar(leitura.mudanca);
  };

  return { campos, erros, falha, salvando, abrir, fechar, mudar, salvar, enviar, limparFalha };
}

export function PainelDoCadastro({
  organizacao,
  aoSalvar,
}: {
  organizacao: OrganizacaoDetalhada;
  /** Salva o cadastro; rejeita com o erro da API, que o cartão mostra. Sem ele, nada se edita. */
  aoSalvar?: (mudanca: MudancaDoCadastro) => Promise<void>;
}): ReactNode {
  const { cadastro, contrato } = organizacao;
  const editavel = aoSalvar !== undefined && organizacao.exclusao === null;
  const empresa = useEdicao<CamposDoCadastro>(
    lerCadastro,
    aoSalvar,
    'Não consegui salvar o cadastro.',
  );
  const contato = useEdicao<CamposDoContato>(
    lerContato,
    aoSalvar,
    'Não consegui salvar o contato financeiro.',
  );
  const contatoFinanceiro = contrato?.contatoFinanceiro ?? null;
  const exclusao = organizacao.exclusao;
  // Na excluída, o contato guardado até a purga ainda se apaga a pedido.
  const apagaNaExcluida =
    aoSalvar !== undefined && exclusao !== null && organizacao.contatoAteAPurga;
  const [confirmandoRemocao, definirConfirmandoRemocao] = useState(false);
  const removivel =
    apagaNaExcluida || (editavel && contato.campos !== null && contatoFinanceiro !== null);

  return (
    <>
      <Cartao variante="folgado" como="section" aria-labelledby="organizacao-cadastro">
        <div className="organizacao__cab-do-painel">
          <h3 id="organizacao-cadastro" className="organizacao__titulo-do-painel">
            Cadastro
          </h3>
          {editavel ? (
            <AcoesDaEdicao
              editando={empresa.campos !== null}
              salvando={empresa.salvando}
              rotulo="Editar cadastro"
              aoAbrir={() => empresa.abrir(camposDoCadastro(cadastro))}
              aoCancelar={empresa.fechar}
              aoSalvar={() => void empresa.salvar()}
            />
          ) : null}
        </div>
        <Ficha
          larguraRotulo="168px"
          linhas={[
            {
              rotulo: 'Razão social',
              valor: empresa.campos ? (
                <span className="organizacao__campo organizacao__campo--largo">
                  <Campo
                    tamanho="miudo"
                    rotuloOculto="Razão social"
                    value={empresa.campos.razaoSocial}
                    erro={empresa.erros.razaoSocial}
                    onChange={(evento) => empresa.mudar('razaoSocial', evento.target.value)}
                  />
                </span>
              ) : (
                (cadastro.razaoSocial ?? 'Não informada')
              ),
            },
            { rotulo: 'Nome fantasia', valor: organizacao.nome },
            {
              rotulo: 'CNPJ',
              valor: empresa.campos ? (
                <span className="organizacao__campo organizacao__campo--largo">
                  {/* Texto, e não só números: o CNPJ alfanumérico tem letras. */}
                  <Campo
                    tamanho="miudo"
                    rotuloOculto="CNPJ"
                    autoCapitalize="characters"
                    spellCheck={false}
                    value={empresa.campos.cnpj}
                    erro={empresa.erros.cnpj}
                    onChange={(evento) => empresa.mudar('cnpj', mascararCnpj(evento.target.value))}
                  />
                </span>
              ) : cadastro.cnpj ? (
                mascararCnpj(cadastro.cnpj)
              ) : (
                'Não informado'
              ),
            },
            {
              rotulo: 'Segmento',
              valor: empresa.campos ? (
                <span className="organizacao__campo organizacao__campo--largo">
                  <CampoEscolha
                    tamanho="miudo"
                    rotuloOculto="Segmento"
                    valor={empresa.campos.segmento}
                    opcoes={OPCOES_DE_SEGMENTO}
                    vazio="Escolha"
                    aoEscolher={(valor) => empresa.mudar('segmento', valor)}
                  />
                </span>
              ) : (
                (cadastro.segmento?.nome ?? 'Não informado')
              ),
            },
            { rotulo: 'Identificador', valor: organizacao.identificador },
            { rotulo: 'Fuso horário', valor: cadastro.fuso },
            { rotulo: 'Cliente desde', valor: diaMesAno(organizacao.desde) },
          ]}
        />
        {empresa.falha ? (
          <p className="organizacao__falha" role="alert">
            {empresa.falha}
          </p>
        ) : null}
      </Cartao>

      <Cartao variante="folgado" como="section" aria-labelledby="organizacao-financeiro">
        <div className="organizacao__cab-do-painel">
          <h3 id="organizacao-financeiro" className="organizacao__titulo-do-painel">
            Contato financeiro
          </h3>
          {/* Apagar a pedido de quem foi indicado: ele sai, e o histórico diz só que saiu. */}
          {removivel && !confirmandoRemocao ? (
            <Botao
              variante="perigo"
              miudo
              desabilitado={contato.salvando}
              motivo="Salvando"
              onClick={() => {
                // A pergunta abre limpa: a falha de antes era de outro pedido, e
                // "Manter o contato" apagaria a dela sem dizer (#1160).
                contato.limparFalha();
                definirConfirmandoRemocao(true);
              }}
            >
              {exclusao ? 'Apagar o contato agora' : 'Remover contato'}
            </Botao>
          ) : null}
          {editavel && contrato ? (
            <AcoesDaEdicao
              editando={contato.campos !== null}
              salvando={contato.salvando}
              rotulo="Editar contato"
              aoAbrir={() => {
                definirConfirmandoRemocao(false);
                contato.abrir(camposDoContato(contatoFinanceiro));
              }}
              aoCancelar={() => {
                definirConfirmandoRemocao(false);
                contato.fechar();
              }}
              aoSalvar={() => void contato.salvar()}
            />
          ) : null}
        </div>
        {exclusao ? (
          // Da excluída, o contato não aparece: a tela diz só se ele ainda está guardado.
          <p className="organizacao__nota">
            {organizacao.contatoAteAPurga
              ? `Guardado até a purga (até ${diaMesAno(exclusao.purgaAte)}), sem aparecer aqui: a ` +
                'gestão desta organização acabou. Se a pessoa indicada pedir, apague agora.'
              : 'Nenhum contato guardado.'}
          </p>
        ) : !contrato ? (
          <p className="organizacao__nota">
            Sem contrato ainda: o contato financeiro fica guardado nele.
          </p>
        ) : (
          <Ficha
            larguraRotulo="168px"
            linhas={(['nome', 'email', 'telefone'] as const).map((campo) => {
              const rotulo = { nome: 'Nome', email: 'E-mail', telefone: 'Telefone' }[campo];
              const gravado = contatoFinanceiro
                ? campo === 'telefone'
                  ? telefoneParaLer(contatoFinanceiro.telefone)
                  : contatoFinanceiro[campo]
                : 'Não informado';
              return {
                rotulo,
                valor: contato.campos ? (
                  <span className="organizacao__campo organizacao__campo--largo">
                    <Campo
                      tamanho="miudo"
                      rotuloOculto={`${rotulo} do contato financeiro`}
                      type={campo === 'email' ? 'email' : campo === 'telefone' ? 'tel' : 'text'}
                      autoComplete="off"
                      value={contato.campos[campo]}
                      erro={contato.erros[campo]}
                      onChange={(evento) => contato.mudar(campo, evento.target.value)}
                    />
                  </span>
                ) : (
                  gravado
                ),
              };
            })}
          />
        )}
        {removivel && confirmandoRemocao ? (
          <div className="organizacao__confirmar">
            <span>
              {exclusao
                ? 'Apagar o contato financeiro? Ele sai agora, antes da purga, e o histórico diz só que saiu.'
                : 'Remover o contato financeiro? Ele sai do cadastro agora, e o histórico diz só que saiu.'}
            </span>
            <Botao
              variante="perigo"
              miudo
              desabilitado={contato.salvando}
              motivo="Salvando"
              onClick={() => {
                // Recusada, a pergunta fica, com o motivo ao lado: a pessoa tenta
                // de novo ou mantém o contato (#1160).
                void contato
                  .enviar({ contatoFinanceiro: null })
                  .then((saiu) => saiu && definirConfirmandoRemocao(false));
              }}
            >
              {exclusao ? 'Apagar' : 'Remover'}
            </Botao>
            <Botao
              variante="fantasma"
              miudo
              desabilitado={contato.salvando}
              motivo="Salvando"
              onClick={() => {
                definirConfirmandoRemocao(false);
                contato.limparFalha();
              }}
            >
              Manter o contato
            </Botao>
          </div>
        ) : null}
        {contato.falha ? (
          <p className="organizacao__falha" role="alert">
            {contato.falha}
          </p>
        ) : null}
        {contrato && !exclusao ? (
          <p className="organizacao__nota">
            Não recebe e-mail do CliqCRM: é para a equipe da Cliqfy consultar na hora da cobrança.
          </p>
        ) : null}
      </Cartao>
    </>
  );
}
